<template>
  <div class="discussion__body">
    <template v-if="normalizedMessages.length">
      <div
        v-for="msg in normalizedMessages"
        :key="msg._key"
        class="messages__box"
      >
        <div class="messages__avatar">
          <img
            v-if="msg.avatar"
            :src="msg.avatar"
            alt=""
          >
          <img
            v-else
            src="@/assets/images/avatar.svg"
            alt=""
          >
        </div>

        <div class="messages-content">
          <div
            class="messages-content__view"
            :class="msg.roleClass"
          >
            <div class="messages-content__head">
              <div class="messages-content__head-left">
                <div class="messages-content__author">{{ msg.author }}</div>
                <div class="messages-content__time">{{ msg.time }}</div>
              </div>

              <a
                class="action-like"
                href="#"
                @click.prevent="onLikeClick(msg)"
              >
                <span :class="msg.liked ? 'icon-like--active' : 'icon-like--notactive'"></span>
                <span class="action-like-num">{{ msg.likes }}</span>
              </a>
            </div>

            <div class="messages-content__text">{{ msg.text }}</div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="messages-empty">
      <p class="mb-0">No messages yet.</p>
    </div>

    <!-- Single input for the whole thread (at the bottom) -->
    <div v-if="!readOnly" class="discussion__comment">
      <div class="discussion__avatar">
        <img
          v-if="meAvatar"
          :src="meAvatar"
          alt=""
        >
        <img
          v-else
          src="@/assets/images/avatar.svg"
          alt=""
        >
      </div>
      <div class="discussion__form">
        <textarea-autosize
          v-model="draft"
          class="growTextarea"
          rows="1"
          :disabled="isSending"
          :placeholder="placeholder"
          @keydown.enter.native="onEnterKeydown"
          @keyup.ctrl.enter.native="onSubmit"
        ></textarea-autosize>
        <button
          class="discussion__btn"
          :disabled="isSending || !canSubmit"
          :aria-busy="isSending ? 'true' : 'false'"
          @click="onSubmit"
        >
          <span
            v-if="isSending"
            class="discussion__btn-spinner"
            aria-hidden="true"
          ></span>
          <span v-else>Submit</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'MessageThread',

  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Comment...',
    },
    isSending: {
      type: Boolean,
      default: false,
    },
    meAvatar: {
      type: String,
      default: '',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    submitOnEnter: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      draft: '',
    };
  },

  computed: {
    canSubmit() {
      return Boolean(String(this.draft || '').trim());
    },

    normalizedMessages() {
      const arr = Array.isArray(this.messages) ? this.messages : [];

      return arr.map((m, idx) => {
        const user = m?.user || m?.author || m?.sender || {};

        const author = user?.name
          || user?.full_name
          || m?.user_name
          || m?.author_name
          || m?.name
          || 'Unknown';

        const avatar = user?.avatar
          || user?.photo
          || m?.avatar
          || '';

        const createdAt = m?.created_at || m?.createdAt || m?.time || m?.date || '';
        const time = this.formatTime(createdAt);

        const text = m?.text || m?.message || m?.body || m?.comment || '';

        const likes = Number(m?.likes_count ?? m?.likes ?? m?.like ?? 0) || 0;
        const liked = Boolean(m?.liked ?? m?.is_liked ?? false);
        const role = this.messageRole(m, user);

        return {
          _key: m?.id || `${createdAt}-${idx}`,
          id: m?.id,
          author,
          avatar,
          time,
          text,
          likes,
          liked,
          role,
          roleClass: role ? `from-${role}` : '',
        };
      });
    },
  },

  methods: {
    async scrollToLastMessage() {
      await this.$nextTick();
      if (!this.$el || !this.$el.querySelectorAll) return;
      const boxes = this.$el.querySelectorAll('.messages__box');
      const last = boxes.length ? boxes[boxes.length - 1] : null;
      if (last && typeof last.scrollIntoView === 'function') {
        last.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },

    messageRole(message, user) {
      const rawRole = user?.role
        || message?.role
        || message?.user_role
        || message?.marketplace_user_role
        || '';
      const role = String(rawRole).toLowerCase();
      if (role === 'seller' || role === 'buyer') return role;

      const rawType = message?.marketplace_user_type || message?.user_type || '';
      const type = Number(rawType);
      if (type === 2) return 'seller';
      if (type === 3) return 'buyer';

      return '';
    },

    onEnterKeydown(event) {
      if (!this.submitOnEnter || event.shiftKey || event.ctrlKey || event.metaKey) return;
      event.preventDefault();
      event.stopPropagation();
      this.onSubmit();
    },
    formatTime(value) {
      if (!value) return '';

      const m = moment(value, [
        moment.ISO_8601,
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DD',
        'DD.MM.YYYY HH:mm',
        'DD.MM.YYYY',
        'DD/MM/YYYY HH:mm',
        'DD/MM/YYYY',
      ], true);

      if (m.isValid()) {
        // Matches the template feel: "2h ago"
        return m.fromNow();
      }

      return String(value);
    },

    onLikeClick(msg) {
      if (!msg || msg.id == null) return;
      if (msg.liked) {
        this.$emit('unlike', msg);
      } else {
        this.$emit('like', msg);
      }
    },

    onSubmit(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (this.isSending || !this.canSubmit) return;

      const text = String(this.draft).trim();
      this.$emit('send', text);
      this.draft = '';
    },
  },
};
</script>

<style scoped>
.messages-empty {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 20px;
}

.discussion__btn {
  min-width: 120px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  transform: translateY(-50%);
  line-height: 1;
}

.discussion__btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-top-color: #fff;
  border-radius: 50%;
  animation: discussion-btn-spin 0.75s linear infinite;
}

.growTextarea:disabled {
  cursor: wait;
  opacity: 0.72;
}

.messages-content__view.from-buyer {
  background-color: #FBF9D3;
}

.messages-content__view.from-seller {
  background-color: #E2EDF0;
}

@keyframes discussion-btn-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
